import React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {IconLink} from '@tabler/icons-react';
import {lightGreen} from '@mui/material/colors';
import {Prompt, StyleSx} from 'app/types/common';
import {Files} from 'app/components/entities/files';
import {DialogClose} from 'app/components/sharedReactComponents/DialogClose/DialogClose';
import {OverflowTooltip} from 'app/components/sharedReactComponents/OverflowTooltip/OverflowTooltip';
import {CopyButton} from 'app/components/shared';

const paperSx: StyleSx = {
  display: 'flex',
  borderColor: 'secondary.main',
  flex: 1,
  gap: 1,
  alignItems: 'center',
  justifyContent: 'space-between',
  alignSelf: 'stretch',
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  borderRight: 0,
};
const buttonSx: StyleSx = {
  minWidth: 112,
  justifyContent: 'flex-start',
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
};

interface Props extends Prompt {
  name: string;
  link?: string;
  share?: Files.Share;
}

export function ShareLinkDialog({name, link, share, open, onClose}: Props) {
  return (
    <Dialog open={open} fullWidth={true} maxWidth="sm" onClose={onClose}>
      <DialogClose onClose={onClose} />

      <DialogTitle>
        Share file: <br />
        <Typography variant="inherit" color="primary.main">
          {name}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{mt: 1, mb: 3}}>
        <Typography textAlign="center" color="text.secondary" mb={1}>
          Anyone with this link can download this video
        </Typography>

        {link ? (
          <Stack mb={2} direction="row" alignItems="center">
            <Paper variant="outlined" sx={paperSx}>
              <Typography px={1} color="primary.main" component="span">
                <OverflowTooltip>{link}</OverflowTooltip>
              </Typography>
            </Paper>

            <CopyButton value={link ?? ''}>
              {({copied, copy}) => {
                return (
                  <Button
                    data-id="copy-button"
                    sx={buttonSx}
                    variant="contained"
                    color="secondary"
                    disabled={!link}
                    startIcon={<IconLink />}
                    onClick={copy}
                  >
                    {copied ? 'Copied' : 'Copy link'}
                  </Button>
                );
              }}
            </CopyButton>
          </Stack>
        ) : (
          <Typography mb={2} textAlign="center">
            Preparing link
          </Typography>
        )}

        <Typography variant="h5" textAlign="center" mb={2}>
          File details:
        </Typography>

        {share && <SharingStats share={share} />}
      </DialogContent>
    </Dialog>
  );
}

interface StatsProps {
  share: Files.Share;
}

function SharingStats({share}: StatsProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      gap={4}
      divider={
        <Divider sx={{borderColor: lightGreen[700]}} orientation="vertical" flexItem={true} />
      }
    >
      <Stack flex={1} alignItems="flex-end">
        <span>
          <Typography component="span" color="text.secondary">
            Downloads:
          </Typography>{' '}
          <Typography component="span" fontWeight={600}>
            {share.downloads}
          </Typography>
        </span>
      </Stack>

      <Stack flex={1} direction="row" gap={0.5} minWidth={0}>
        <Typography color="text.secondary" whiteSpace="nowrap">
          Shared by:
        </Typography>

        <Typography component="span" fontWeight={600} minWidth={0}>
          <OverflowTooltip>{share.authorName}</OverflowTooltip>
        </Typography>
      </Stack>
    </Stack>
  );
}
